import { useEffect, useState } from 'react';
import { api, type ApiError, type AuditEntry, type EnvName } from '../api';
import { ago, Empty, ErrorState, fullDate, PageHeader, Spinner } from '../components/ui';
import { useResource } from '../hooks';
import { translate as tt, useT, type MessageKey } from '../i18n';
import { auditLine } from '../auditWords';
import { money } from '../money';
import { useShell } from './ProjectShell';
import { projectEyebrow } from '../projectNav';

const PAGE = 100;

// action → 人が読む一文（表示中の言語）。一文の表は auditWords.ts（サーバーが書く種類を全部持つ・auditWords.test.ts が突き合わせる）。
// ここに残すのは訳した種類名・金額の書式が要る 2 つだけ。表に無い action（他モジュールが新しく足したもの）はそのまま出す
function describe(e: AuditEntry): string {
  const d = (e.detail ?? {}) as Record<string, unknown>;
  switch (e.action) {
    case 'key.create': {
      // kind は publishable / secret。既知なら訳した種別名（「公開可能キー」）を差し込む
      const kind = d.kind === 'publishable' || d.kind === 'secret' ? tt(`key.kind.${d.kind}`) : String(d.kind ?? '');
      return tt('audit.keyCreate', { kind });
    }
    case 'payments.refund':
      return typeof d.amount === 'number' && typeof d.currency === 'string'
        ? tt('audit.paymentsRefund', { amount: money(d.amount, d.currency) })
        : e.action;
  }
  const line = auditLine(e.action, d);
  // キーは auditWords.ts の表から来る（辞書にあることは auditWords.test.ts が確かめる）= MessageKey に寄せてよい
  return line ? tt(line.key as MessageKey, line.vars) : e.action;
}

function actorLabel(e: AuditEntry) {
  // 共同作業者の操作はサーバーが伏せ字のメールを付ける（2026-10-22 core/handoff-1b）= 役割と並べて出す
  if (e.actorRole === 'collaborator') return e.actorEmail ? `${tt('audit.collaborator')} · ${e.actorEmail}` : tt('audit.collaborator');
  if (e.actorEmail) return e.actorEmail;
  if (e.actorRole === 'owner') return tt('audit.owner');
  // 他の開発者の操作はメールが来ない（受け取ったプロジェクトに前の持ち主のメールを出さない = サーバーの audit の WHY）。UUID ではなく役割で言う
  if (e.actorRole === 'previous_owner') return tt('audit.previousOwner');
  if (e.actorRole === 'other_developer') return tt('audit.otherDeveloper');
  if (e.actor.startsWith('key:')) return tt('audit.secretKey', { id: e.actor.slice(4, 12) });
  return e.actor;
}

export function AuditPage() {
  const { project, env } = useShell();
  const [scope, setScope] = useState<'all' | EnvName>('all');
  const url = `/v1/projects/${project.id}/audit?limit=${PAGE}${scope !== 'all' ? `&env=${scope}` : ''}`;
  const r = useResource<{ entries: AuditEntry[]; nextBefore: number | null }>(url);
  const { t } = useT();
  const [more, setMore] = useState<AuditEntry[]>([]);
  const [before, setBefore] = useState<number | null>(null);
  useEffect(() => {
    setMore([]);
    setBefore(r.data?.nextBefore ?? null);
  }, [r.data]);

  // さらに読み込むの失敗と処理中（以前は catch も処理中の表示も無く、失敗しても何も出なかった。dashboard-review 2026-09-27）
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState<ApiError | null>(null);
  const loadMore = async () => {
    if (!before) return;
    setLoadingMore(true);
    setMoreError(null);
    try {
      const next = await api<{ entries: AuditEntry[]; nextBefore: number | null }>(`${url}&before=${before}`);
      setMore((m) => [...m, ...next.entries]);
      setBefore(next.nextBefore);
    } catch (e) {
      setMoreError(e as ApiError);
    } finally {
      setLoadingMore(false);
    }
  };
  const entries = [...(r.data?.entries ?? []), ...more];

  return (
    <>
      <PageHeader eyebrow={projectEyebrow(project)} title={t('audit.title')}>
        <div className="seg" role="group" aria-label={t('audit.filter')}>
          {(['all', 'development', 'production'] as const).map((s) => (
            <button key={s} type="button" className={`seg-btn ${scope === s ? 'is-active' : ''}`} aria-pressed={scope === s} onClick={() => setScope(s)}>
              {s === 'all' ? t('audit.all') : s === env ? t('audit.current', { env: t(`env.inline.${s}`) }) : t(`env.inline.${s}`)}
            </button>
          ))}
        </div>
      </PageHeader>
      {r.loading && !r.data && <Spinner />}
      {r.error && <ErrorState error={r.error} what={t('audit.title')} onRetry={r.reload} />}
      {r.data && entries.length === 0 && <Empty title={t('audit.emptyTitle')}>{t('audit.emptyBody')}</Empty>}
      {entries.length > 0 && (
        <ol className="audit">
          {entries.map((e) => (
            <li key={e.id} className={`audit-row audit-${e.action.split('.')[0]}`}>
              <time dateTime={e.createdAt} title={fullDate(e.createdAt)}>
                {ago(e.createdAt)}
              </time>
              <span className="audit-action mono">{e.action}</span>
              <span className="audit-desc">{describe(e)}</span>
              <span className="audit-meta small muted">
                {actorLabel(e)}
                {e.environment && <span className={`env-chip env-chip-${e.environment}`}>{t(`env.chip.${e.environment}`)}</span>}
              </span>
            </li>
          ))}
        </ol>
      )}
      {before && (
        <div className="load-more">
          {moreError && <ErrorState error={moreError} onRetry={() => void loadMore()} />}
          <button type="button" className="btn btn-ghost" onClick={() => void loadMore()} disabled={loadingMore}>
            {loadingMore ? t('common.loadingMore') : t('audit.loadOlder')}
          </button>
        </div>
      )}
    </>
  );
}
